import { z } from 'zod';
/**
 * The complete list of active national currencies from the ISO 4217 list. This
 * is the canonical source for currency names and codes across Owner apps.
 *
 * ⚠️ Note that this IS NOT a complete enumeration of ISO 4217; some currencies
 * deemed unlikely to be relevant to Owner's business are omitted. Specifically,
 * "X currencies" (funds, precious metals, supranational currencies...) and
 * withdrawn (i.e. inactive) currencies are omitted, along with a handful of
 * secondary/specialized currencies, like BOV in Bolivia and UYW in Uruguay. Any
 * omitted country could be added later on if business needs change.
 *
 * Data was sourced from https://github.com/datasets/currency-codes.
 *
 * See {@link OPERATING_CURRENCIES} for a list of currencies constrained only to
 * countries where Owner currently operates.
 */
export const CURRENCIES = [
    { code: 'AED', minorUnit: 2, name: 'UAE Dirham' },
    { code: 'AFN', minorUnit: 2, name: 'Afghani' },
    { code: 'ALL', minorUnit: 2, name: 'Lek' },
    { code: 'AMD', minorUnit: 2, name: 'Armenian Dram' },
    { code: 'AOA', minorUnit: 2, name: 'Kwanza' },
    { code: 'ARS', minorUnit: 2, name: 'Argentine Peso' },
    { code: 'AUD', minorUnit: 2, name: 'Australian Dollar' },
    { code: 'AWG', minorUnit: 2, name: 'Aruban Florin' },
    { code: 'AZN', minorUnit: 2, name: 'Azerbaijan Manat' },
    { code: 'BAM', minorUnit: 2, name: 'Convertible Mark' },
    { code: 'BBD', minorUnit: 2, name: 'Barbados Dollar' },
    { code: 'BDT', minorUnit: 2, name: 'Taka' },
    { code: 'BHD', minorUnit: 3, name: 'Bahraini Dinar' },
    { code: 'BIF', minorUnit: 0, name: 'Burundi Franc' },
    { code: 'BMD', minorUnit: 2, name: 'Bermudian Dollar' },
    { code: 'BND', minorUnit: 2, name: 'Brunei Dollar' },
    { code: 'BOB', minorUnit: 2, name: 'Boliviano' },
    { code: 'BRL', minorUnit: 2, name: 'Brazilian Real' },
    { code: 'BSD', minorUnit: 2, name: 'Bahamian Dollar' },
    { code: 'BTN', minorUnit: 2, name: 'Ngultrum' },
    { code: 'BWP', minorUnit: 2, name: 'Pula' },
    { code: 'BYN', minorUnit: 2, name: 'Belarusian Ruble' },
    { code: 'BZD', minorUnit: 2, name: 'Belize Dollar' },
    { code: 'CAD', minorUnit: 2, name: 'Canadian Dollar' },
    { code: 'CDF', minorUnit: 2, name: 'Congolese Franc' },
    { code: 'CHF', minorUnit: 2, name: 'Swiss Franc' },
    { code: 'CLP', minorUnit: 0, name: 'Chilean Peso' },
    { code: 'CNY', minorUnit: 2, name: 'Yuan Renminbi' },
    { code: 'COP', minorUnit: 2, name: 'Colombian Peso' },
    { code: 'CRC', minorUnit: 2, name: 'Costa Rican Colon' },
    { code: 'CUP', minorUnit: 2, name: 'Cuban Peso' },
    { code: 'CVE', minorUnit: 2, name: 'Cabo Verde Escudo' },
    { code: 'CZK', minorUnit: 2, name: 'Czech Koruna' },
    { code: 'DJF', minorUnit: 0, name: 'Djibouti Franc' },
    { code: 'DKK', minorUnit: 2, name: 'Danish Krone' },
    { code: 'DOP', minorUnit: 2, name: 'Dominican Peso' },
    { code: 'DZD', minorUnit: 2, name: 'Algerian Dinar' },
    { code: 'EGP', minorUnit: 2, name: 'Egyptian Pound' },
    { code: 'ERN', minorUnit: 2, name: 'Nakfa' },
    { code: 'ETB', minorUnit: 2, name: 'Ethiopian Birr' },
    { code: 'EUR', minorUnit: 2, name: 'Euro' },
    { code: 'FJD', minorUnit: 2, name: 'Fiji Dollar' },
    {
        code: 'FKP',
        minorUnit: 2,
        name: 'Falkland Islands Pound',
    },
    { code: 'GBP', minorUnit: 2, name: 'Pound Sterling' },
    { code: 'GEL', minorUnit: 2, name: 'Lari' },
    { code: 'GHS', minorUnit: 2, name: 'Ghana Cedi' },
    { code: 'GIP', minorUnit: 2, name: 'Gibraltar Pound' },
    { code: 'GMD', minorUnit: 2, name: 'Dalasi' },
    { code: 'GNF', minorUnit: 0, name: 'Guinean Franc' },
    { code: 'GTQ', minorUnit: 2, name: 'Quetzal' },
    { code: 'GYD', minorUnit: 2, name: 'Guyana Dollar' },
    { code: 'HKD', minorUnit: 2, name: 'Hong Kong Dollar' },
    { code: 'HNL', minorUnit: 2, name: 'Lempira' },
    { code: 'HTG', minorUnit: 2, name: 'Gourde' },
    { code: 'HUF', minorUnit: 2, name: 'Forint' },
    { code: 'IDR', minorUnit: 2, name: 'Rupiah' },
    { code: 'ILS', minorUnit: 2, name: 'New Israeli Sheqel' },
    { code: 'INR', minorUnit: 2, name: 'Indian Rupee' },
    { code: 'IQD', minorUnit: 3, name: 'Iraqi Dinar' },
    { code: 'IRR', minorUnit: 2, name: 'Iranian Rial' },
    { code: 'ISK', minorUnit: 0, name: 'Iceland Krona' },
    { code: 'JMD', minorUnit: 2, name: 'Jamaican Dollar' },
    { code: 'JOD', minorUnit: 3, name: 'Jordanian Dinar' },
    { code: 'JPY', minorUnit: 0, name: 'Yen' },
    { code: 'KES', minorUnit: 2, name: 'Kenyan Shilling' },
    { code: 'KGS', minorUnit: 2, name: 'Som' },
    { code: 'KHR', minorUnit: 2, name: 'Riel' },
    { code: 'KMF', minorUnit: 0, name: 'Comorian Franc' },
    { code: 'KPW', minorUnit: 2, name: 'North Korean Won' },
    { code: 'KRW', minorUnit: 0, name: 'Won' },
    { code: 'KWD', minorUnit: 3, name: 'Kuwaiti Dinar' },
    {
        code: 'KYD',
        minorUnit: 2,
        name: 'Cayman Islands Dollar',
    },
    { code: 'KZT', minorUnit: 2, name: 'Tenge' },
    { code: 'LAK', minorUnit: 2, name: 'Lao Kip' },
    { code: 'LBP', minorUnit: 2, name: 'Lebanese Pound' },
    { code: 'LKR', minorUnit: 2, name: 'Sri Lanka Rupee' },
    { code: 'LRD', minorUnit: 2, name: 'Liberian Dollar' },
    { code: 'LSL', minorUnit: 2, name: 'Loti' },
    { code: 'LYD', minorUnit: 3, name: 'Libyan Dinar' },
    { code: 'MAD', minorUnit: 2, name: 'Moroccan Dirham' },
    { code: 'MDL', minorUnit: 2, name: 'Moldovan Leu' },
    { code: 'MGA', minorUnit: 2, name: 'Malagasy Ariary' },
    { code: 'MKD', minorUnit: 2, name: 'Denar' },
    { code: 'MMK', minorUnit: 2, name: 'Kyat' },
    { code: 'MNT', minorUnit: 2, name: 'Tugrik' },
    { code: 'MOP', minorUnit: 2, name: 'Pataca' },
    { code: 'MRU', minorUnit: 2, name: 'Ouguiya' },
    { code: 'MUR', minorUnit: 2, name: 'Mauritius Rupee' },
    { code: 'MVR', minorUnit: 2, name: 'Rufiyaa' },
    { code: 'MWK', minorUnit: 2, name: 'Malawi Kwacha' },
    { code: 'MXN', minorUnit: 2, name: 'Mexican Peso' },
    { code: 'MYR', minorUnit: 2, name: 'Malaysian Ringgit' },
    { code: 'MZN', minorUnit: 2, name: 'Mozambique Metical' },
    { code: 'NAD', minorUnit: 2, name: 'Namibia Dollar' },
    { code: 'NGN', minorUnit: 2, name: 'Naira' },
    { code: 'NIO', minorUnit: 2, name: 'Cordoba Oro' },
    { code: 'NOK', minorUnit: 2, name: 'Norwegian Krone' },
    { code: 'NPR', minorUnit: 2, name: 'Nepalese Rupee' },
    { code: 'NZD', minorUnit: 2, name: 'New Zealand Dollar' },
    { code: 'OMR', minorUnit: 3, name: 'Rial Omani' },
    { code: 'PEN', minorUnit: 2, name: 'Sol' },
    { code: 'PGK', minorUnit: 2, name: 'Kina' },
    { code: 'PHP', minorUnit: 2, name: 'Philippine Peso' },
    { code: 'PKR', minorUnit: 2, name: 'Pakistan Rupee' },
    { code: 'PLN', minorUnit: 2, name: 'Zloty' },
    { code: 'PYG', minorUnit: 0, name: 'Guarani' },
    { code: 'QAR', minorUnit: 2, name: 'Qatari Rial' },
    { code: 'RON', minorUnit: 2, name: 'Romanian Leu' },
    { code: 'RSD', minorUnit: 2, name: 'Serbian Dinar' },
    { code: 'RUB', minorUnit: 2, name: 'Russian Ruble' },
    { code: 'RWF', minorUnit: 0, name: 'Rwanda Franc' },
    { code: 'SAR', minorUnit: 2, name: 'Saudi Riyal' },
    {
        code: 'SBD',
        minorUnit: 2,
        name: 'Solomon Islands Dollar',
    },
    { code: 'SCR', minorUnit: 2, name: 'Seychelles Rupee' },
    { code: 'SDG', minorUnit: 2, name: 'Sudanese Pound' },
    { code: 'SEK', minorUnit: 2, name: 'Swedish Krona' },
    { code: 'SGD', minorUnit: 2, name: 'Singapore Dollar' },
    { code: 'SHP', minorUnit: 2, name: 'Saint Helena Pound' },
    { code: 'SLE', minorUnit: 2, name: 'Leone' },
    { code: 'SOS', minorUnit: 2, name: 'Somali Shilling' },
    { code: 'SRD', minorUnit: 2, name: 'Surinam Dollar' },
    {
        code: 'SSP',
        minorUnit: 2,
        name: 'South Sudanese Pound',
    },
    { code: 'STN', minorUnit: 2, name: 'Dobra' },
    { code: 'SYP', minorUnit: 2, name: 'Syrian Pound' },
    { code: 'SZL', minorUnit: 2, name: 'Lilangeni' },
    { code: 'THB', minorUnit: 2, name: 'Baht' },
    { code: 'TJS', minorUnit: 2, name: 'Somoni' },
    {
        code: 'TMT',
        minorUnit: 2,
        name: 'Turkmenistan New Manat',
    },
    { code: 'TND', minorUnit: 3, name: 'Tunisian Dinar' },
    { code: 'TOP', minorUnit: 2, name: 'Pa’anga' },
    { code: 'TRY', minorUnit: 2, name: 'Turkish Lira' },
    {
        code: 'TTD',
        minorUnit: 2,
        name: 'Trinidad and Tobago Dollar',
    },
    { code: 'TWD', minorUnit: 2, name: 'New Taiwan Dollar' },
    { code: 'TZS', minorUnit: 2, name: 'Tanzanian Shilling' },
    { code: 'UAH', minorUnit: 2, name: 'Hryvnia' },
    { code: 'UGX', minorUnit: 0, name: 'Uganda Shilling' },
    { code: 'USD', minorUnit: 2, name: 'US Dollar' },
    { code: 'UYU', minorUnit: 2, name: 'Peso Uruguayo' },
    { code: 'UZS', minorUnit: 2, name: 'Uzbekistan Sum' },
    { code: 'VED', minorUnit: 2, name: 'Bolívar Soberano' },
    { code: 'VND', minorUnit: 0, name: 'Dong' },
    { code: 'VUV', minorUnit: 0, name: 'Vatu' },
    { code: 'WST', minorUnit: 2, name: 'Tala' },
    { code: 'XAF', minorUnit: 0, name: 'Central African CFA franc' },
    { code: 'XCD', minorUnit: 2, name: 'East Caribbean Dollar' },
    { code: 'XCG', minorUnit: 2, name: 'Caribbean Guilder' },
    { code: 'XOF', minorUnit: 0, name: 'West African CFA Franc' },
    { code: 'XPF', minorUnit: 0, name: 'CFP Franc' },
    { code: 'YER', minorUnit: 2, name: 'Yemeni Rial' },
    { code: 'ZAR', minorUnit: 2, name: 'Rand' },
    { code: 'ZMW', minorUnit: 2, name: 'Zambian Kwacha' },
    { code: 'ZWG', minorUnit: 2, name: 'Zimbabwe Gold' },
];
/** The ISO codes for all currencies defined in {@link CURRENCIES}. */
export const CURRENCY_CODES = CURRENCIES.map(({ code }) => code);
/** Zod schema for a {@link CurrencyCode}. */
export const currencyCodeSchema = z.enum(CURRENCY_CODES);
const CURRENCIES_BY_CODE = Object.freeze(Object.fromEntries(CURRENCIES.map((x) => [x.code, x])));
/** Looks up a {@link Currency} by its {@link CurrencyCode}. */
export const lookupCurrency = (currencyCode) => CURRENCIES_BY_CODE[currencyCode];
//# sourceMappingURL=currencies.js.map